import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Box, ChevronRight, Film, Layers, Plus, Sparkles, Star } from 'lucide-react';

import {
  useGetGalleryQuery,
  useGetMyAvatarsQuery,
  type AvatarItem,
} from '@/services/avatarApi';
import { AvatarCard } from '@/components/avatars/AvatarCard';
import { AvatarCarousel } from '@/components/avatars/AvatarCarousel';
import { AvatarSkeleton } from '@/components/avatars/AvatarSkeleton';
import {
  AvatarFilters,
  applyAvatarFilter,
  type AvatarFilter,
  type AvatarView,
} from '@/components/avatars/AvatarFilters';
import { useMainButton } from '@/hooks/useMainButton';
import { useHaptics } from '@/hooks/useHaptics';
import { usePuoCreareAvatar } from '@/hooks/usePuoCreareAvatar';
import { env } from '@/env';
import { cn } from '@/lib/cn';
import { useLinguaFormato } from '@/hooks/useLinguaFormato';
import { formatData } from '@/lib/fmt';

type Tab = 'mine' | 'explore';

const VIEW_STORAGE_KEY = 'qlash.avatar.view';
const FILTER_STORAGE_KEY = 'qlash.avatar.filter';

function readPersisted<T extends string>(key: string, allowed: readonly T[], fallback: T): T {
  if (typeof window === 'undefined') return fallback;
  const v = window.localStorage.getItem(key);
  return v && (allowed as readonly string[]).includes(v) ? (v as T) : fallback;
}

/**
 * `scheda` (sezione Avatar a schede, I-635, solo col flag `VITE_FEATURE_AVATAR_SCHEDE_ENABLED`): dice quale delle due liste mostrare quando la pagina sta
 * dentro `AvatarSezione`. Con il flag SPENTO (produzione) e' ignorata e la pagina e' quella di oggi, al nodo: tutto cio' che cambia sta dietro `inSezione`.
 */
export function Avatars({ scheda }: { scheda?: Tab }) {
  const navigate = useNavigate();
  const { t } = useTranslation('avatars');
  const haptics = useHaptics();
  // I-416, L-2762: con il flag Studio acceso (solo staging) la creazione e' di `administrator` (Qavatar#10 la riserva
  // lato server). Chi non ha il ruolo sfoglia e sceglie fra gli avatar che ci sono: niente «+», niente «Crea», niente
  // editor, niente «Crea il tuo primo». Flag spento: gli ingressi sono quelli di oggi (I-270).
  const puoCreare = usePuoCreareAvatar();
  // DUE letture del flag delle schede, come per emote e negozio: `env` per i test, il confronto LETTERALE perche' il bundler lo piega.
  // `inSezione`: la pagina e' una scheda di `AvatarSezione` (testata e schede stanno fuori; qui niente righe-ingresso, niente «+», niente
  // doppio selettore: «Editor», «Emote» e «Negozio» sono schede). Flag spento: `scheda` non conta e il resto e' invariato.
  const tabFissa = env.featureAvatarSchedeEnabled && import.meta.env.VITE_FEATURE_AVATAR_SCHEDE_ENABLED === 'true' ? scheda : undefined;
  const inSezione = tabFissa !== undefined;
  // 2026-05-20 — default to My Avatars (Luca's preference: users care about
  // their own work first; Explore is secondary). Explore stays as a tap-away.
  const [tabLocale, setActiveTab] = useState<Tab>('mine');
  const activeTab: Tab = tabFissa ?? tabLocale;
  const [view, setView] = useState<AvatarView>(() =>
    readPersisted(VIEW_STORAGE_KEY, ['grid', 'list'] as const, 'grid'),
  );
  const [filter, setFilter] = useState<AvatarFilter>(() =>
    readPersisted(FILTER_STORAGE_KEY, ['all', '3d', 'animated', 'featured', 'photo'] as const, 'all'),
  );

  useEffect(() => {
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, view); } catch { /* ignore */ }
  }, [view]);
  useEffect(() => {
    try { window.localStorage.setItem(FILTER_STORAGE_KEY, filter); } catch { /* ignore */ }
  }, [filter]);

  const galleryQuery = useGetGalleryQuery({ page: 1, limit: 50 });
  const myQuery = useGetMyAvatarsQuery({ page: 1, limit: 50 });

  const data = activeTab === 'explore' ? galleryQuery.data : myQuery.data;
  const isLoading = activeTab === 'explore' ? galleryQuery.isLoading : myQuery.isLoading;
  const isError = activeTab === 'explore' ? galleryQuery.isError : myQuery.isError;
  const allItems = data?.items ?? [];

  // The filter bar only applies to the "mine" tab. Explore stays a flat feed.
  const filteredItems = useMemo(
    () => (activeTab === 'mine' ? applyAvatarFilter(allItems, filter) : allItems),
    [activeTab, allItems, filter],
  );

  const switchTab = useCallback(
    (tab: Tab) => {
      if (tab !== activeTab) {
        haptics.selection();
        setActiveTab(tab);
      }
    },
    [activeTab, haptics],
  );

  const onFilterChange = useCallback(
    (f: AvatarFilter) => {
      haptics.selection();
      setFilter(f);
    },
    [haptics],
  );

  const onViewChange = useCallback(
    (v: AvatarView) => {
      haptics.selection();
      setView(v);
    },
    [haptics],
  );

  const goCreate = useCallback(() => {
    haptics.impact('light');
    navigate('/avatars/new');
  }, [navigate, haptics]);

  useMainButton(
    puoCreare && !inSezione
      ? {
          text: t('list.create'),
          onClick: goCreate,
        }
      : null,
  );

  return (
    <div className="flex flex-col gap-4 px-4 pb-24 pt-5">
      {/* I-318, ADR-0190 §1: titolo di sezione fuori dalla vista, `sr-only`; la chiave resta in uso. E anche il sottotitolo; il bottone resta, a destra. */}
      {!inSezione && (
      <header className="flex items-start justify-end">
        <div className="sr-only">
          <h1>{t('list.title')}</h1>
          <p>
            {t('list.subtitle')}
          </p>
        </div>
        {puoCreare && (
          <button
            type="button"
            onClick={goCreate}
            aria-label={t('list.create')}
            className={cn(
              'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
              'bg-brand text-brand-fg transition-opacity active:opacity-80',
            )}
          >
            <Plus aria-hidden className="h-5 w-5" />
          </button>
        )}
      </header>
      )}

      {/* Entry point to the wizard-style Avatar Editor. Moved here from the
          Hub (UX decision 2026-05-26) so the editor lives inside the avatar
          section rather than as a Hub CTA. */}
      {puoCreare && !inSezione && (
        <Link
          to="/avatars/editor"
          className="flex items-center justify-between rounded-2xl border border-dashed border-brand/40 bg-brand/5 px-4 py-3 text-sm transition-opacity active:opacity-80"
        >
          <div className="flex items-center gap-3">
            <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand/15 text-brand">
              <Film aria-hidden className="h-5 w-5" />
            </span>
            <div className="flex flex-col">
              <span className="font-bold text-fg">{t('list.editorTitle')}</span>
              <span className="text-[11px] text-fg-muted">
                {t('list.editorSubtitle')}
              </span>
            </div>
          </div>
          <span className="rounded-full bg-brand/15 px-2 py-0.5 text-[10px] font-semibold uppercase text-brand">
            {t('list.beta')}
          </span>
        </Link>
      )}

      {/* I-581, ADR-0218: «Le mie emote» — scegliere fino a 6 clip dell'avatar in vetrina da mandare al tavolo. Solo staging (flag). */}
      {/* DUE letture del flag (v. `router.tsx`): `env` per i test, il confronto LETTERALE perche' il bundler piega il ramo e il marcatore
          `avatars-le-mie-emote` non finisce nella build di produzione. */}
      {!inSezione && env.featureAvatarEmotesEnabled && import.meta.env.VITE_FEATURE_AVATAR_EMOTES_ENABLED === 'true' && (
        <Link
          to="/avatars/emotes"
          data-testid="avatars-le-mie-emote"
          className="flex items-center justify-between rounded-2xl bg-bg-secondary px-4 py-3 text-sm transition-opacity active:opacity-80"
        >
          <div className="flex flex-col">
            <span className="font-bold text-fg">{t('emotes.entryTitle')}</span>
            <span className="text-[11px] text-fg-muted">{t('emotes.entrySubtitle')}</span>
          </div>
          <ChevronRight aria-hidden className="h-5 w-5 text-fg-muted" />
        </Link>
      )}

      {/* I-612: il negozio degli avatar, SOLO il riscatto gratuito. Solo staging (flag), stesse DUE letture del flag di «Le mie emote»:
          il marcatore `avatars-negozio` non finisce nella build di produzione. */}
      {!inSezione && env.featureAvatarShopEnabled && import.meta.env.VITE_FEATURE_AVATAR_SHOP_ENABLED === 'true' && (
        <Link
          to="/avatars/negozio"
          data-testid="avatars-negozio"
          className="flex items-center justify-between rounded-2xl bg-bg-secondary px-4 py-3 text-sm transition-opacity active:opacity-80"
        >
          <div className="flex min-w-0 flex-col">
            <span className="font-bold text-fg">{t('shop.entryTitle')}</span>
            <span className="text-[11px] text-fg-muted">{t('shop.entrySubtitle')}</span>
          </div>
          <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
        </Link>
      )}

      {!inSezione && (
      <div role="tablist" className="flex gap-1 rounded-xl bg-bg-secondary p-1">
        {(['mine', 'explore'] as const).map((tab) => (
          <button
            key={tab}
            type="button"
            role="tab"
            aria-selected={activeTab === tab}
            onClick={() => switchTab(tab)}
            className={cn(
              'flex-1 rounded-lg py-2 text-sm font-semibold transition-colors',
              activeTab === tab
                ? 'bg-brand text-brand-fg'
                : 'bg-transparent text-fg-muted',
            )}
          >
            {tab === 'mine' ? t('list.tabMine') : t('list.tabExplore')}
          </button>
        ))}
      </div>
      )}

      {activeTab === 'mine' && allItems.length > 0 && (
        <AvatarFilters
          items={allItems}
          activeFilter={filter}
          view={view}
          onFilterChange={onFilterChange}
          onViewChange={onViewChange}
        />
      )}

      {isLoading && <AvatarSkeleton />}

      {isError && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-danger">
          {t('list.loadFailed')}
        </div>
      )}

      {!isLoading && !isError && filteredItems.length === 0 && (
        <div className="rounded-xl bg-bg-secondary p-6 text-center text-sm text-fg-muted">
          {activeTab === 'explore' ? (
            // Chi non puo' creare (flag Studio acceso, non-admin) non deve leggere «Crea tu il primo!»: senza ingresso
            // e' un invito senza azione (revisione di tma-orcb, #616).
            t(puoCreare ? 'list.emptyExplore' : 'list.emptyExploreReserved')
          ) : allItems.length === 0 ? (
            t(puoCreare ? 'list.emptyMine' : 'list.emptyMineReserved')
          ) : (
            <>{t('list.emptyFiltered')} <button type="button" onClick={() => onFilterChange('all')} className="text-brand underline">{t('list.showAll')}</button>.</>
          )}
          {allItems.length === 0 && puoCreare && (
            <Link
              to="/avatars/new"
              className="mt-3 inline-block rounded-lg bg-brand px-4 py-2 text-sm font-semibold text-brand-fg"
            >
              {t('list.createFirst')}
            </Link>
          )}
        </div>
      )}

      {filteredItems.length > 0 && (
        activeTab === 'mine' ? (
          // 2026-05-20 — "My avatars" lists ONE compact row per series. Tap to
          // drill into all variations. Replaces the per-variation flat stack
          // because that doesn't scale once a user has 10+ avatars × 3-5 variants
          // each. Statistics chips (count, 3D, animated, skins, video clips,
          // featured) summarize the series at a glance.
          <div className="flex flex-col gap-2">
            {groupBySeries(filteredItems).map((series) => (
              <SeriesRow
                key={series.seriesId}
                series={series}
                onOpen={() => { haptics.impact('light'); navigate(`/avatars/${series.latest._id}/series`); }}
              />
            ))}
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {filteredItems.map((avatar) => renderAvatar(avatar))}
          </div>
        )
      )}
    </div>
  );
}

function renderAvatar(avatar: AvatarItem) {
  const versions = avatar.seriesCount ?? 1;
  if (versions > 1) {
    return <AvatarCarousel key={avatar._id} avatar={avatar} />;
  }
  return <AvatarCard key={avatar._id} avatar={avatar} />;
}

interface SeriesGroup {
  seriesId: string;
  latest: AvatarItem;
  variants: AvatarItem[];
  count: number;
  /** Aggregated capabilities across all variants in the series. */
  has3d: boolean;
  hasAnimated: boolean;
  hasSkins: boolean;
  hasVideoClips: boolean;
  hasFeatured: boolean;
}

/**
 * Group a flat list of avatars by seriesId. Picks the latest (newest createdAt)
 * as the series cover. `count` is the visible-from-this-page count — which may
 * be lower than `latest.seriesCount` (server-side total) when the user has more
 * variants than the current `limit`. We surface BOTH so the user knows when to
 * drill in for the rest.
 */
function groupBySeries(items: AvatarItem[]): SeriesGroup[] {
  const byId = new Map<string, SeriesGroup>();
  for (const a of items) {
    const sid = a.seriesId;
    const entry = byId.get(sid);
    if (entry) {
      entry.variants.push(a);
      if (new Date(a.createdAt) > new Date(entry.latest.createdAt)) entry.latest = a;
      entry.has3d        ||= a.mesh?.status === 'ready' || a.mesh?.status === 'animated_ready';
      entry.hasAnimated  ||= a.mesh?.status === 'animated_ready' || ((a.mesh?.animationPacks?.length ?? 0) > 0);
      entry.hasSkins     ||= (a.mesh?.skins?.length ?? 0) > 0;
      entry.hasVideoClips ||= (a.videoClips?.length ?? 0) > 0;
      entry.hasFeatured  ||= !!a.isFeatured;
    } else {
      byId.set(sid, {
        seriesId: sid,
        latest: a,
        variants: [a],
        count: 0, // filled below from latest.seriesCount
        has3d: a.mesh?.status === 'ready' || a.mesh?.status === 'animated_ready',
        hasAnimated: a.mesh?.status === 'animated_ready' || ((a.mesh?.animationPacks?.length ?? 0) > 0),
        hasSkins: (a.mesh?.skins?.length ?? 0) > 0,
        hasVideoClips: (a.videoClips?.length ?? 0) > 0,
        hasFeatured: !!a.isFeatured,
      });
    }
  }
  // Prefer server-side seriesCount when available — handles partial pagination.
  const list = Array.from(byId.values()).map((s) => ({
    ...s,
    count: s.latest.seriesCount ?? s.variants.length,
  }));
  // Newest series first by latest.createdAt.
  list.sort((a, b) => new Date(b.latest.createdAt).getTime() - new Date(a.latest.createdAt).getTime());
  return list;
}

interface SeriesRowProps {
  series: SeriesGroup;
  onOpen: () => void;
}

function SeriesRow({ series, onOpen }: SeriesRowProps) {
  const lingua = useLinguaFormato();
  const { t } = useTranslation('avatars');
  const { latest, count, has3d, hasAnimated, hasSkins, hasVideoClips, hasFeatured } = series;
  const sub = `${t('list.variants', { count })} · ${formatData(latest.createdAt, undefined, lingua)}`;
  return (
    <button
      type="button"
      onClick={onOpen}
      className={cn(
        'flex items-center gap-3 rounded-2xl border border-border bg-bg-secondary p-2 text-left',
        'transition-opacity active:opacity-80',
      )}
    >
      <img
        src={latest.thumbnailUrl ?? latest.imageUrl}
        alt={latest.title}
        className="h-14 w-14 shrink-0 rounded-xl border border-black/40 bg-black object-cover"
      />
      <div className="min-w-0 flex-1">
        <p className="line-clamp-1 text-sm font-bold text-fg">{latest.title}</p>
        <p className="mt-0.5 line-clamp-1 text-[11px] text-fg-muted">{sub}</p>
        <div className="mt-1 flex flex-wrap items-center gap-1.5">
          {hasFeatured && (
            <span className="flex items-center gap-1 rounded-full bg-brand/20 px-1.5 py-0.5 text-[10px] font-semibold text-brand">
              <Star aria-hidden className="h-2.5 w-2.5 fill-current" /> {t('filters.chip.hub')}
            </span>
          )}
          {has3d && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Box aria-hidden className="h-2.5 w-2.5" /> {t('filters.chip.threeD')}
            </span>
          )}
          {hasAnimated && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Layers aria-hidden className="h-2.5 w-2.5" /> {t('list.badgeAnim')}
            </span>
          )}
          {hasSkins && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Sparkles aria-hidden className="h-2.5 w-2.5" /> {t('list.badgeSkins')}
            </span>
          )}
          {hasVideoClips && (
            <span className="flex items-center gap-1 rounded-full bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-fg-muted">
              <Film aria-hidden className="h-2.5 w-2.5" /> {t('list.badgeVideo')}
            </span>
          )}
        </div>
      </div>
      <ChevronRight aria-hidden className="h-5 w-5 shrink-0 text-fg-muted" />
    </button>
  );
}
